/* eslint-disable no-unused-vars */
/* eslint-disable import/no-unresolved */

'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Autoplay, Pagination, Keyboard, A11y,
} from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';
import DOMPurify from 'dompurify';
import './promoCards.scss';
import PropTypes from 'prop-types';
import {
  getInitialIndexSlide, useIsMobile, getResponsiveImageUrl, focusSlideIntoView,
} from 'helpers/ui';
import { FallbackImage } from '@rcw/ui';
import { goToCustomUrl } from 'controls/utils';
import { CTAButton } from 'components/atoms/Button/Button';

const getOverlayGradient = (alignment) => {
  const strong = 'rgba(0, 0, 0, 0.7)';
  const transparent = 'rgba(0, 0, 0, 0)';

  if (alignment === 'right') {
    return `linear-gradient(to left, ${strong} 0%, ${strong} 55%, ${transparent} 85%)`;
  }
  if (alignment === 'center') {
    return `linear-gradient(to right, ${transparent} 0%, ${strong} 25%, ${strong} 75%, ${transparent} 100%)`;
  }
  return `linear-gradient(to right, ${strong} 0%, ${strong} 52%, ${transparent} 85%)`;
};

export default function PromoCards({ data }) {
  const swiperRef = useRef(null);
  const paginationRef = useRef(null);
  const isMobile = useIsMobile();
  const [paginationReady, setPaginationReady] = useState(false);

  const promoData = (data?.elements || [])
    .filter((e) => e && e.enabled)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  useEffect(() => {
    if (paginationRef.current) setPaginationReady(true);
  }, []);

  const initialIndexSlide = getInitialIndexSlide(promoData.length);

  return (
    <div className='promo' onFocus={(e) => focusSlideIntoView(swiperRef.current, e.target)}>
      {paginationReady && (
      <Swiper
        className='promoCarouselContainer'
        modules={[Pagination, Keyboard, A11y]}
        autoplay={false}
        initialSlide={initialIndexSlide}
        pagination={{
          clickable: true,
        }}
        keyboard={{ enabled: true, onlyInViewport: true }}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
          swiper.params.pagination.el = paginationRef.current;
        }}
        onAfterInit={(swiper) => {
          swiper.pagination.init();
          swiper.pagination.render();
          swiper.pagination.update();
          swiper.update();
        }}
        slidesPerView='auto'
        centeredSlides
      >
        {promoData.map((element, idx) => (
          <SwiperSlide key={element.order ?? element.title ?? idx} className='promoCarouselSlide'>
            <div
              className='flex promoCarouselContainer'
              style={{ justifyContent: element.alignment }}
            >
              <div
                className='promosOverlay'
                aria-hidden='true'
                style={{ background: getOverlayGradient(element.alignment) }}
              />
              <div
                className='promosDataContainer'
                style={{
                  textAlign: element.alignment,
                  alignItems: element.alignment,
                }}
              >
                {element.topTitle && (
                <div className='promosDataContainer_topTitle' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.topTitle) }} />
                )}
                {element.title && (
                <div
                  className='promosDataContainer_title proxima-nova-bold'
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.title) }}
                />
                )}
                {element.body && (
                <div className='promosDataContainer_body' dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(element.body) }} />
                )}
                {element.ctaText && element.ctaUrl && (
                <div className='flex' style={{ justifyContent: element.alignment }}>
                  <CTAButton
                    ctaText={element.ctaText}
                    onClick={() => goToCustomUrl(element.ctaUrl)}
                  />
                </div>
                )}
              </div>
              <FallbackImage
                imgClassName='promosImg'
                src={getResponsiveImageUrl(element.imageUrl, element.mobileImageUrl, isMobile)}
                fallbackSrc={element.imageUrl}
                alt={`slide-${idx + 1}`}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      )}
      <div className='promoPagination' ref={paginationRef} />
    </div>
  );
}

PromoCards.propTypes = {
  data: PropTypes.shape({
    elements: PropTypes.arrayOf(
      PropTypes.shape({
        enabled: PropTypes.bool,
        order: PropTypes.number,
        title: PropTypes.string,
        topTitle: PropTypes.string,
        body: PropTypes.string,
        alignment: PropTypes.oneOf(['left', 'center', 'right']),
        ctaText: PropTypes.string,
        ctaUrl: PropTypes.string,
        imageUrl: PropTypes.string,
        mobileImageUrl: PropTypes.string,
      }),
    ),
  }),
};
